import React from 'react'
import SearchResults from '@chegg-wt/core/components/SearchResults'
import { Region } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { SourceType, JournalType, Source, AppRoute } from '@chegg-wt/core/types/core'
import { useSelector } from 'react-redux'
import { searchWidgetExpEnabledSelector } from '@chegg-wt/core/state/createCitation/selectors'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import sourceTypes from '@chegg-wt/core/constants/sourceTypes'
import SourceTypeLinkPicker from '@chegg-wt/core/components/SourceTypeLinkPicker'
import CitationSearchBar from '@chegg-wt/core/components/CitationSearchBar/index'
import { useIsLaptopOrSmallerScreen } from '@chegg-wt/core/hooks/useScreenSize'
import { Config } from '@chegg-wt/core/config'
import { isProSelector } from '@chegg-wt/core/state/user/selectors'
import CitationSearchInput from '../../CitationSearchInput'
import { Box } from '../../Layouts/SourceTypeSelection/styled'
import { CITATION_SEARCH } from './utils/testIds'
import { Container } from './styled'

export interface CitationSearchProps {
  afterFirstSource?: React.ReactNode
  afterSecondSource?: React.ReactNode
  isOverflow?: boolean
  searchText: string
  citationStyle: string
  sourceType: SourceType
  initialSearchQuery?: string
  loading: boolean
  error: boolean
  sources: Source[]
  journalType: JournalType
  invalidSearch: boolean
  sourceSearchQuery: string
  onChangeSourceType: (sourceType: SourceType) => void
  onChangeSearchQuery: (searchQuery: string) => void
  onChangeJournalType: (journalType: JournalType) => void
  onSelectSource: (source: Source, index?: number) => void
  onManualCiteClick: (region: Region) => void
}

const CitationSearch: React.FC<CitationSearchProps> = ({
  afterFirstSource,
  afterSecondSource,
  isOverflow = true,
  searchText,
  citationStyle,
  sourceType: currentSourceType,
  initialSearchQuery,
  loading,
  error,
  sources,
  journalType,
  invalidSearch,
  sourceSearchQuery,
  onChangeSourceType,
  onChangeSearchQuery,
  onChangeJournalType,
  onSelectSource,
  onManualCiteClick,
}) => {
  const isSearchWidgetExpEnabled = useSelector(searchWidgetExpEnabledSelector)
  const { toManualCitation, toSourceSearch, toSourceSelection } = useRoutesContext()
  const composeSourceTypeLink = (sourceType: SourceType): AppRoute => {
    const routeParams = { style: citationStyle, sourceType: sourceType.id }
    if (sourceType.manual) {
      return toManualCitation({ ...routeParams })
    }
    if (isSearchWidgetExpEnabled)
      return toSourceSelection({ ...routeParams, query: sourceSearchQuery })
    return toSourceSearch({ ...routeParams })
  }
  const dropdownSourceTypes = [...sourceTypes]
  const buttonSourceTypes = dropdownSourceTypes.splice(0, 3)

  const isLaptopOrSmaller = useIsLaptopOrSmallerScreen()
  const { appName } = Config
  const isPaidEbUser = useSelector(isProSelector) && appName === 'eb'
  const showNewCitationSearchWidget =
    isSearchWidgetExpEnabled && !isLaptopOrSmaller && !isPaidEbUser

  return (
    <Container data-test={CITATION_SEARCH}>
      {showNewCitationSearchWidget ? (
        <>
          <Box>
            <SourceTypeLinkPicker
              buttonSourceTypes={buttonSourceTypes}
              dropdownSourceTypes={dropdownSourceTypes}
              composeRoute={composeSourceTypeLink}
              onSourceTypeSelect={onChangeSourceType}
              selectedSourceId={currentSourceType.id}
            />
          </Box>
          <CitationSearchBar initialSearchQuery={initialSearchQuery} />
        </>
      ) : (
        <CitationSearchInput
          onChangeSourceType={onChangeSourceType}
          sourceType={currentSourceType}
          initialSearchQuery={initialSearchQuery}
          onChangeSearchQuery={onChangeSearchQuery}
          onManualCiteClick={onManualCiteClick}
        />
      )}
      <SearchResults
        afterFirstSource={afterFirstSource}
        afterSecondSource={afterSecondSource}
        isOverflow={isOverflow}
        searchText={searchText}
        citationStyle={citationStyle}
        sourceType={currentSourceType}
        sources={sources}
        loading={loading}
        error={error}
        journalType={journalType}
        invalidSearch={invalidSearch}
        onSelectSource={onSelectSource}
        onChangeJournalType={onChangeJournalType}
        onChangeSourceType={onChangeSourceType}
        onManualCiteClick={onManualCiteClick}
      />
    </Container>
  )
}

export default CitationSearch
